import SrhUiI18nService from 'srh-ui/dist/services/i18n';
import { TypeNotification, useGlobalStore } from '@/store/globalStore';
import KeycloakService from '@/services/keycloakService';
import { callApi, HTTPMethods } from '@/utils/api';
import { getApiUrl } from '@/utils/url';
import type { LienResponse, CreateLienDTO } from '@/types/api';

export default {
  /** GET /liens/admin → LienResponse[] */
  async loadAdminLinks(
    onSuccess?: (links: LienResponse[]) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate().then(async () => {
        const config = {
          'Content-Type': 'application/json',
        };
        await callApi({
          path: `${getApiUrl()}/liens/admin`,
          method: HTTPMethods.GET,
          options: config,
          successCallback: (result: LienResponse[]) => {
            result.forEach(link => {
              if (link.s3Key?.includes('.svg') && link.imageBase64) {
                link.imageBase64 = link.imageBase64.replace(
                  'data:application/octet-stream',
                  'data:image/svg+xml'
                );
              }
            });
            onSuccess?.(result);
          },
          errorMessage: t('errors.favorite'),
        });
      });
    } catch (e: unknown) {
      onError?.(e);
      if (!onError) {
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
      }
    }
  },

  /** POST /liens/admin with CreateLienDTO (FormData) */
  async createLinkByAdmin(
    linkData: CreateLienDTO & { file?: File },
    onSuccess?: (result: LienResponse) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();

      const formData = new FormData();
      formData.append('name', linkData.name);
      formData.append('url', linkData.url);
      formData.append('userId', linkData.userId);

      if (linkData.file) {
        formData.append('file', linkData.file);
      }

      await callApi({
        path: `${getApiUrl()}/liens/admin`,
        method: HTTPMethods.POST,
        body: formData,
        options: {},
        successCallback: (result: any) => {
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t('messages.linkCreated'),
            t('messages.success')
          );
          onSuccess?.(result);
        },
        errorMessage: t('errors.createLink'),
      });
    } catch (e: any) {
      onError?.(e) ??
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
    }
  },

  /** PATCH /liens/admin/:id */
  async updateLinkByAdmin(
    linkId: string,
    linkData: { name?: string; url?: string; file?: File },
    onSuccess?: (result: LienResponse) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();

      const formData = new FormData();
      if (linkData.name) formData.append('name', linkData.name);
      if (linkData.url) formData.append('url', linkData.url);
      if (linkData.file) formData.append('file', linkData.file);

      await callApi({
        path: `${getApiUrl()}/liens/admin/${linkId}`,
        method: HTTPMethods.PATCH,
        body: formData,
        options: {},
        successCallback: (result: any) => {
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t('messages.linkUpdated'),
            t('messages.success')
          );
          onSuccess?.(result);
        },
        errorMessage: t('errors.updateLink'),
      });
    } catch (e: any) {
      onError?.(e) ??
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
    }
  },

  /** DELETE /liens/admin/:id */
  async deleteLinkByAdmin(
    linkId: string,
    onSuccess?: () => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();
      const config = {
        'Content-Type': 'application/json',
      };

      await callApi({
        path: `${getApiUrl()}/liens/admin/${linkId}`,
        method: HTTPMethods.DELETE,
        options: config,
        successCallback: () => {
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t('messages.linkDeleted'),
            t('messages.success')
          );
          onSuccess?.();
        },
        errorMessage: t('errors.deleteLink'),
      });
    } catch (e: any) {
      onError?.(e) ??
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
    }
  },
};
